import axios from 'axios';
import { toast } from 'sonner';
import { logger } from './logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Auth tokens are stored in localStorage and sent as Authorization: Bearer
 * headers in ADDITION to the httpOnly cookie set by the backend.
 *
 * This is required because iOS Safari's Intelligent Tracking Prevention (ITP)
 * blocks cross-site cookies (the frontend lives on sportsweeps.io, the backend
 * on war-room-test.emergent.host). The bearer fallback lets authenticated
 * sessions work on iOS Safari/Chrome/FF, which all use WebKit.
 *
 * Desktop/Android still use the cookie happily, so this is a belt-and-braces
 * approach that doesn't regress anything.
 */
const TOKEN_KEYS = {
  admin: 'sm_admin_token',
  player: 'sm_player_token',
};

const safeGet = (k) => {
  try { return localStorage.getItem(k); }
  catch (err) {
    // Safari Private Mode / disabled storage — non-fatal but log so devs see it.
    if (process.env.NODE_ENV !== 'production') console.debug('[api] localStorage read blocked:', k, err?.message);
    return null;
  }
};

const safeSet = (k, v) => {
  try { localStorage.setItem(k, v); }
  catch (err) {
    if (process.env.NODE_ENV !== 'production') console.debug('[api] localStorage write blocked:', k, err?.message);
  }
};

const safeRemove = (k) => {
  try { localStorage.removeItem(k); }
  catch (err) {
    if (process.env.NODE_ENV !== 'production') console.debug('[api] localStorage remove blocked:', k, err?.message);
  }
};

export const tokenStore = {
  getAdmin: () => safeGet(TOKEN_KEYS.admin),
  setAdmin: (t) => safeSet(TOKEN_KEYS.admin, t),
  clearAdmin: () => safeRemove(TOKEN_KEYS.admin),
  getPlayer: () => safeGet(TOKEN_KEYS.player),
  setPlayer: (t) => safeSet(TOKEN_KEYS.player, t),
  clearPlayer: () => safeRemove(TOKEN_KEYS.player),
  clearAll: () => { safeRemove(TOKEN_KEYS.admin); safeRemove(TOKEN_KEYS.player); },
};

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  // 15s default protects every endpoint from hanging the UI on a slow query,
  // scheduler lock, or network blip. Long-running calls (large uploads,
  // report exports) can opt out per request with `api.post(url, body, { timeout: 60000 })`.
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const url = config.url || '';
  const isPlayerRoute = url.startsWith('/player/') || url.includes('/player/');
  const token = isPlayerRoute ? tokenStore.getPlayer() : tokenStore.getAdmin();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const url = err.config?.url || '';
      const isPlayerRoute = url.startsWith('/player/') || url.includes('/player/');
      if (!isPlayerRoute) {
        // Single prefix check covers every admin route under /admin/* —
        // dash, tournaments, branding, billing, settings, season, profile,
        // live, and any future additions. Previous explicit-path list was
        // missing /admin/season, /admin/profile, /admin/live, which left
        // admins stuck on a broken page when their session expired.
        const path = window.location.pathname;
        if (path.startsWith('/admin/')) {
          tokenStore.clearAdmin();
          window.location.href = '/login';
        }
      }
    }

    // Timeout handling: axios marks aborted requests with `ECONNABORTED`
    // and a message starting with "timeout of". Surface a friendly toast
    // so users see "something happened" instead of a silent dead spinner,
    // and warn in the console for devs debugging slow endpoints.
    const isTimeout =
      err.code === 'ECONNABORTED' ||
      (typeof err.message === 'string' && err.message.toLowerCase().includes('timeout'));
    if (isTimeout) {
      logger.warn('[api] Request timeout:', err.config?.url);
      // Single toast per timeout - sonner dedupes by id so rapid back-to-back
      // timeouts don't stack into a wall of red.
      toast.error('Request timed out. Please check your connection and try again.', { id: 'api-timeout' });
    }

    return Promise.reject(err);
  }
);

/* ─── In-memory TTL cache for stable endpoints ─────────────────────────────
 * The admin app re-fetches the same slow-changing data on every page mount:
 * `/group/branding`, `/billing/status`, `/group/currency`, etc. Each call
 * costs ~120 ms, so flipping between pages racks up half a second of pure
 * waiting on data that rarely changes. We wrap `api.get` so requests for
 * whitelisted URLs are served from memory inside the TTL window, while
 * everything else (catalog, leaderboards, focus data) still hits the wire.
 *
 *  • Whitelist is **exact path match** so we never accidentally cache a
 *    parametrised endpoint like `/activated-tournaments/{id}`.
 *  • Cache key includes the stringified `params` so different filters don't
 *    collide.
 *  • Returned objects are deep-cloned via structuredClone so callers can
 *    safely mutate without poisoning the next reader.
 *  • Any non-GET request to a cached path invalidates that path's entry,
 *    so a `PUT /group/branding` immediately refreshes everyone else.
 *  • `api.invalidate(url)` and `api.clearCache()` let pages opt-in to
 *    forcing a fresh fetch (e.g. after a webhook tells us billing changed).
 */
const CACHE_TTL_MS = 30_000; // 30 s - page-flip horizon, not a real cache
const CACHE_WHITELIST = new Set([
  // Admin / society-scoped
  '/group/branding',
  '/group/currency',
  '/group/me',
  '/group/payment-keys',
  '/billing/status',
  '/tournaments/launch-window',
  '/auth/me',
  // Player-scoped — these change rarely once a player is in a pool, so the
  // same 30 s page-flip horizon also pays off when bouncing between
  // dashboard, war room, and live pages.
  '/player/me',
  '/player/dashboard',
  '/player/email-preferences',
]);

const responseCache = new Map(); // url+params → { at, value }
const inFlight = new Map();      // url+params → Promise — dedup parallel fetches

const cacheKey = (url, params) => `${url}|${params ? JSON.stringify(params) : ''}`;

const cloneResponse = (cached) => {
  // axios responses contain config + headers; we only need `.data` to be
  // independent. structuredClone deep-copies the data, the rest is shared.
  try {
    return { ...cached, data: structuredClone(cached.data) };
  } catch {
    return { ...cached, data: cached.data }; // structuredClone fails on funcs/DOM → fall back
  }
};

const rawGet = api.get.bind(api);
api.get = function cachedGet(url, config) {
  if (!CACHE_WHITELIST.has(url)) return rawGet(url, config);
  const key = cacheKey(url, config?.params);
  const now = Date.now();
  const cached = responseCache.get(key);
  if (cached && now - cached.at < CACHE_TTL_MS) {
    return Promise.resolve(cloneResponse(cached.value));
  }
  // Dedup overlapping fetches — if Dashboard and Tournaments both mount on
  // the same tick, only one HTTP request goes out.
  const existing = inFlight.get(key);
  if (existing) return existing;
  const p = rawGet(url, config)
    .then((res) => {
      responseCache.set(key, { at: Date.now(), value: res });
      return cloneResponse(res);
    })
    .finally(() => { inFlight.delete(key); });
  inFlight.set(key, p);
  return p;
};

// Auto-invalidate the cache on any write to a whitelisted path.
const mutatingMethods = ['post', 'put', 'patch', 'delete'];
for (const method of mutatingMethods) {
  const orig = api[method].bind(api);
  api[method] = function invalidatingMutator(url, ...rest) {
    if (CACHE_WHITELIST.has(url)) {
      // Clear every cache entry that targets this URL regardless of params.
      for (const k of responseCache.keys()) {
        if (k.startsWith(`${url}|`)) responseCache.delete(k);
      }
    }
    return orig(url, ...rest);
  };
}

api.invalidate = (url) => {
  for (const k of responseCache.keys()) {
    if (k.startsWith(`${url}|`)) responseCache.delete(k);
  }
};
api.clearCache = () => responseCache.clear();

export default api;
